/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMemo } from 'react';

import { ConfigRevision, Node, NodeConfig, PaginatedNodesResponse } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { NodesColumns } from '@/pages/Nodes/getNodesColumns';
import { NodesTable } from '@/pages/Nodes/NodesTable';
import { Card } from '@/primitives/Card';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

import { NewFleetFormFields } from './types';

const REVIEW_NODES_COLUMNS: NodesColumns[] = [
  'hostname',
  'operatingSystem',
  'appliedOperationalState',
  'appliedConfiguration',
  'connectivityState',
  'lastSeen',
];

export type ReviewStepProps = {
  tenantId: string;
  formValues: NewFleetFormFields;
  selectedConfig: NodeConfig;
  selectedRevision: ConfigRevision;
  selectedNodes: Node[];
  getNodeConfigurationPath: (nodeConfigId: string) => string;
};

export function ReviewStep({
  tenantId,
  formValues,
  selectedConfig,
  selectedRevision,
  selectedNodes,
  getNodeConfigurationPath,
}: ReviewStepProps) {
  const { t } = useMimicTranslation('fleets');

  const reviewNodesData: PaginatedNodesResponse = useMemo(
    () => ({
      data: selectedNodes,
      meta: {
        page: {
          currentPage: 1,
          pageSize: selectedNodes.length,
          totalItems: selectedNodes.length,
          totalPages: 1,
        },
        filters: {},
        sort: [],
      },
    }),
    [selectedNodes],
  );

  return (
    <Flex gap="md" full>
      <Flex vertical gap="md">
        <Card dataTestId="fleet-review-details" padding="sm" bordered>
          <Flex vertical gap="sm">
            <Title level={5}>{t('wizard.review.fleetDetails')}</Title>
            <Flex vertical gap="xs">
              <Flex gap="md">
                <Text type="subtle" style={{ minWidth: 140 }}>
                  {t('wizard.review.name')}
                </Text>
                <Text>{formValues.name}</Text>
              </Flex>
              <Flex gap="md">
                <Text type="subtle" style={{ minWidth: 140 }}>
                  {t('wizard.review.description')}
                </Text>
                <Text>{formValues.description || '-'}</Text>
              </Flex>
              <Flex gap="md">
                <Text type="subtle" style={{ minWidth: 140 }}>
                  {t('wizard.review.operatingSystem')}
                </Text>
                <Text>{formValues.operatingSystem}</Text>
              </Flex>
            </Flex>
          </Flex>
        </Card>

        <Card dataTestId="fleet-review-config" padding="sm" bordered>
          <Flex vertical gap="sm">
            <Title level={5}>{t('wizard.review.configuration')}</Title>
            <Flex vertical gap="xs">
              <Flex gap="md">
                <Text type="subtle" style={{ minWidth: 140 }}>
                  {t('wizard.review.name')}
                </Text>
                <Text>{selectedConfig.name}</Text>
              </Flex>
              <Flex gap="md">
                <Text type="subtle" style={{ minWidth: 140 }}>
                  {t('wizard.review.revision')}
                </Text>
                <Text>#{selectedRevision.revisionNumber}</Text>
              </Flex>
              <Flex gap="md">
                <Text type="subtle" style={{ minWidth: 140 }}>
                  {t('wizard.review.configId')}
                </Text>
                <Text mono copyable>
                  {selectedConfig.id}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      <div style={{ flex: 1 }}>
        <Card dataTestId="fleet-review-nodes" padding="sm" bordered>
          <div style={{ width: '100%' }}>
            <Title level={5}>{t('wizard.review.selectedNodes', { count: selectedNodes.length })}</Title>
            {selectedNodes.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                <NodesTable
                  tenantID={tenantId}
                  nodesData={reviewNodesData}
                  onParamsChange={() => {}}
                  columns={REVIEW_NODES_COLUMNS}
                  tags={[]}
                  getNodeConfigurationPath={getNodeConfigurationPath}
                  hideFilters
                />
              </div>
            ) : (
              <Text type="subtle">{t('wizard.review.noNodesSelected')}</Text>
            )}
          </div>
        </Card>
      </div>
    </Flex>
  );
}
